'use client';

import { useEffect } from 'react';
import type React from 'react';
import { usePathname } from 'next/navigation';
import { runOnFirstInteractionOrTimeout } from '@/lib/deferred-script';

// Env override so staging never loads the prod container (set
// NEXT_PUBLIC_GTM_ID=disabled there); unset env keeps prod behavior.
const GOOGLE_TAG_MANAGER_ID = process.env.NEXT_PUBLIC_GTM_ID || 'GTM-5XL9S9J';
const GTM_DISABLED = process.env.NEXT_PUBLIC_GTM_ID === 'disabled';
const GTM_SRC_PREFIX = 'https://www.googletagmanager.com/gtm.js';

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[];
  }
}

function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

/**
 * Deferred Google Tag Manager loader.
 *
 * PERF CONTRACT: GTM must NOT be injected eagerly (inline in <head> or via
 * next/script beforeInteractive/afterInteractive). The container pulls 4+
 * gtag configs plus HubSpot analytics (~700KB JS, ~1s main-thread on
 * mobile), which alone drops the PageSpeed mobile score by ~30 points.
 * It loads on first user interaction or after a 10s fallback via
 * runOnFirstInteractionOrTimeout — see src/lib/deferred-script.ts and
 * tests/unit/perf/perf-guards.test.ts before changing this.
 *
 * window.dataLayer is initialized on mount, so any pushes made before
 * gtm.js loads are queued and replayed by GTM as normal.
 */
export function GoogleTagManagerScript(): null {
  const pathname = usePathname();

  useEffect(() => {
    if (GTM_DISABLED || isAdminPath(pathname)) {
      return;
    }

    window.dataLayer = window.dataLayer || [];

    return runOnFirstInteractionOrTimeout(() => {
      if (document.querySelector(`script[src^="${GTM_SRC_PREFIX}"]`)) {
        return;
      }
      window.dataLayer?.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
      const script = document.createElement('script');
      script.async = true;
      script.src = `${GTM_SRC_PREFIX}?id=${GOOGLE_TAG_MANAGER_ID}`;
      document.head.appendChild(script);
    });
  }, [pathname]);

  return null;
}

export function GoogleTagManagerNoScript(): React.JSX.Element | null {
  const pathname = usePathname();

  if (GTM_DISABLED || isAdminPath(pathname)) {
    return null;
  }

  return (
    <noscript>
      <iframe
        src={`https://www.googletagmanager.com/ns.html?id=${GOOGLE_TAG_MANAGER_ID}`}
        height="0"
        width="0"
        style={{ display: 'none', visibility: 'hidden' }}
        title="Google Tag Manager"
      />
    </noscript>
  );
}
